import { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Bell, Check, UserPlus, Coins, Heart, MessageCircle,
  Trophy, Sparkles, AlertTriangle, Loader2,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICONS = {
  follow:            UserPlus,
  tip_received:      Coins,
  post_liked:        Heart,
  post_commented:    MessageCircle,
  comment_replied:   MessageCircle,
  leaderboard_won:   Trophy,
  render_completed:  Sparkles,
  render_failed:     AlertTriangle,
  render_cancelled:  AlertTriangle,
  system:            Bell,
};

const renderNotificationCopy = (n) => {
  const p = n.payload || {};
  switch (n.type) {
    case "follow":
      return `${p.follower_name || "Someone"} started following you.`;
    case "tip_received":
      return `${p.from_user_name || "Someone"} tipped you ${p.amount || 0} XEA.`;
    case "post_liked":
      return `${p.liker_name || "Someone"} liked your post.`;
    case "post_commented":
      return `${p.commenter_name || "Someone"} commented on your post.`;
    case "comment_replied":
      return `${p.replier_name || "Someone"} replied to your comment.`;
    case "leaderboard_won":
      return `🏆 You ranked #${p.rank || "?"} on this weekend's Top 20!`;
    case "render_completed":
      return `Your ${p.kind || "render"} is ready.`;
    case "render_failed":
      return `Your render didn't finish. No tokens charged.`;
    case "render_cancelled":
      return `Your render was cancelled.`;
    default:
      return p.message || "Update.";
  }
};


const NotificationsBell = ({ testIdSuffix = "" }) => {
  const { token } = useAuth();
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  // Initial fetch + 30s poll
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/profiles/notifications?limit=20`, { headers: auth });
        if (!cancelled) {
          setItems(r.data.items || []);
          setUnread(r.data.unread || 0);
        }
      } catch (_e) { /* silent */ }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [token, auth]);

  // Click-outside
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markAllRead = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/profiles/notifications/read-all`, {}, { headers: auth });
      setItems(items.map(i => ({ ...i, read: true })));
      setUnread(0);
    } catch (_e) { /* silent */ }
    finally { setLoading(false); }
  };

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/profiles/notifications/${id}/read`, {}, { headers: auth });
      setItems(items.map(i => i.id === id ? { ...i, read: true } : i));
      setUnread(Math.max(0, unread - 1));
    } catch (_e) { /* silent */ }
  };

  if (!token) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid={`notifications-bell${testIdSuffix}`}
        className="relative w-10 h-10 rounded-full hover:bg-ink-100 flex items-center justify-center text-ink-700"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            data-testid="notifications-unread-count"
            className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-black flex items-center justify-center tabular-nums"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notifications-dropdown"
          className="absolute top-12 right-0 w-80 max-w-[calc(100vw-1rem)] bg-white border border-ink-200 rounded-2xl shadow-xl overflow-hidden z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-ink-100">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-700">
              Notifications
            </p>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                disabled={loading}
                data-testid="notifications-mark-all"
                className="text-[10px] font-bold text-blue-500 hover:underline flex items-center gap-1"
              >
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="text-center text-xs text-ink-500 py-10" data-testid="notifications-empty">
                You're all caught up 🎉
              </p>
            ) : (
              items.map(n => {
                const Icon = TYPE_ICONS[n.type] || Bell;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => markRead(n.id)}
                    data-testid={`notification-${n.id}`}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left border-b border-ink-50 hover:bg-ink-50 ${
                      !n.read ? "bg-blue-50/40" : ""
                    }`}
                  >
                    <span className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      !n.read ? "bg-blue-500 text-white" : "bg-ink-100 text-ink-500"
                    }`}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-ink-950">{renderNotificationCopy(n)}</p>
                      <p className="text-[10px] text-ink-500 mt-1">
                        {new Date(n.created_at).toLocaleString()}
                      </p>
                    </div>
                    {!n.read && (
                      <span className="shrink-0 w-2 h-2 rounded-full bg-blue-500 mt-2" />
                    )}
                  </button>
                );
              })
            )}
          </div>
          <div className="px-4 py-3 border-t border-ink-100 text-center">
            <Link
              to="/dashboard/profile"
              onClick={() => setOpen(false)}
              data-testid="notifications-profile-link"
              className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 hover:text-ink-950"
            >
              Manage profile →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};


export default NotificationsBell;
